import { BACKEND_URL } from '@/config';
﻿import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Check, AlertCircle, Package, Loader } from 'lucide-react';

const API = BACKEND_URL + '/api';

/**
 * VerifyPage — Public page (no auth required) to scan-confirm a delivery QR.
 * The recipient scans the QR on the parcel, lands here, and confirms reception
 * to release funds. Works with `/verify/:token`.
 */
export default function VerifyPage() {
  const { token } = useParams();
  const [decoded, setDecoded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    axios.get(`${API}/qr/decode/${token}`).then(r => {
      if (!active) return;
      setDecoded(r.data);
      setLoading(false);
      if (!r.data.valid) setError('QR code invalide ou expiré.');
    }).catch(() => { if (active) { setError('Lien invalide.'); setLoading(false); } });
    return () => { active = false; };
  }, [token]);

  const confirm = async () => {
    if (decoded.step !== 'delivery') {
      setError('Ce QR n\'est pas un code de livraison. Demandez le bon code à l\'expéditeur.');
      return;
    }
    setConfirming(true);
    try {
      const { data } = await axios.post(`${API}/qr/verify-delivery`, { token });
      setConfirmed(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur lors de la confirmation.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div data-testid="verify-page" style={{
      minHeight: '100vh', background: 'linear-gradient(155deg, #0E1B33, #1E2D45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      fontFamily: 'var(--f-display)', color: '#fff',
    }}>
      <div style={{
        background: '#fff', color: '#0E1B33', borderRadius: 22,
        padding: 36, maxWidth: 440, width: '100%', boxShadow: '0 24px 56px rgba(0,0,0,0.35)',
        textAlign: 'center', animation: 'pop 0.4s cubic-bezier(.16,1,.3,1)',
      }}>
        <style>{`@keyframes pop { from { opacity:0; transform: scale(.94) translateY(8px); } to { opacity:1; transform: scale(1) translateY(0); } }`}</style>

        <div style={{ marginBottom: 14, fontFamily: 'var(--f-display)', fontSize: 18, fontWeight: 800 }}>
          Colis<span style={{ color: '#D4A14A', fontStyle: 'italic' }}>2</span>Ship
        </div>

        {loading && (
          <div style={{ padding: '20px 0', color: 'rgba(14,27,51,0.6)' }}>
            <Loader size={28} style={{ animation: 'spin 1s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ marginTop: 10 }}>Vérification du QR...</div>
          </div>
        )}

        {error && (
          <div data-testid="verify-error" style={{ padding: 20 }}>
            <div style={{ width: 70, height: 70, borderRadius: '50%', background: 'rgba(220,38,38,0.10)', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <AlertCircle size={36} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>QR invalide</h2>
            <p style={{ fontSize: 14, color: 'rgba(14,27,51,0.6)' }}>{error}</p>
          </div>
        )}

        {!loading && decoded && decoded.valid && !confirmed && !error && (
          <>
            <div style={{ width: 70, height: 70, borderRadius: '50%', background: 'rgba(212,161,74,0.12)', color: '#D4A14A', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Package size={32} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Confirmer la réception</h2>
            <p style={{ fontSize: 14, color: 'rgba(14,27,51,0.6)', marginBottom: 16, lineHeight: 1.6 }}>
              {decoded.already_verified
                ? 'Cette livraison a déjà été confirmée.'
                : decoded.step === 'pickup'
                  ? 'Ce QR est un code de prise en charge — il doit être scanné par le transporteur.'
                  : 'Avez-vous bien reçu votre colis ?'}
            </p>
            {decoded.route && (
              <div style={{ background: '#F5F1E8', borderRadius: 12, padding: '14px 16px', marginBottom: 18, fontSize: 14 }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, color: 'rgba(14,27,51,0.5)', marginBottom: 4 }}>Trajet</div>
                <div style={{ fontWeight: 800 }}>{decoded.route}</div>
                {decoded.weight && <div style={{ fontSize: 12, color: 'rgba(14,27,51,0.6)', marginTop: 4 }}>{decoded.weight} kg</div>}
              </div>
            )}
            {!decoded.already_verified && decoded.step === 'delivery' && (
              <button
                data-testid="confirm-delivery-btn"
                onClick={confirm}
                disabled={confirming}
                style={{
                  width: '100%', padding: '14px 24px', borderRadius: 50,
                  border: 'none', background: confirming ? 'rgba(91,175,175,0.5)' : '#5BAFAF',
                  color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer',
                  fontFamily: 'inherit', letterSpacing: 0.3,
                  transition: 'all 0.2s',
                }}
              >
                {confirming ? 'Confirmation...' : '✓ Confirmer la réception'}
              </button>
            )}
          </>
        )}

        {confirmed && (
          <div data-testid="verify-success" style={{ padding: 12 }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(91,175,175,0.14)', color: '#5BAFAF', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
              <Check size={42} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Livraison confirmée 🎉</h2>
            <p style={{ fontSize: 14, color: 'rgba(14,27,51,0.65)', lineHeight: 1.6 }}>
              Merci ! Une preuve cryptographique a été enregistrée et l'expéditeur a été notifié.
              Les fonds peuvent maintenant être libérés au transporteur.
            </p>
            <div style={{ marginTop: 18, fontSize: 11, color: 'rgba(14,27,51,0.4)' }}>
              Vérifié le {confirmed.verified_at ? new Date(confirmed.verified_at).toLocaleString('fr-FR') : '·'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
